import { createContext, useToast } from '@hover/blueprint';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useHistory } from 'react-router';

import { PollGeneratedImage } from 'src/modules/homeownerApp/components/PollGeneratedImage';
import { useHomeownerTranslate } from 'src/modules/homeownerApp/hooks';
import { GeneratedImageStatus } from 'src/modules/homeownerApp/static_values';
import type {
  AiGeneratedImageFragment,
  AiGeneratedImageSegmentationQuery,
  CustomizeAiImageMutationVariables,
} from 'src/shared/graphql/generated';
import {
  useAiGeneratedImageQuery,
  useCustomizeAiImageMutation,
} from 'src/shared/graphql/generated';
import { useMemoContextValue } from 'src/shared/hooks';
import { Route } from 'src/shared/route';

import { AI_GENERATION_APP_ID } from '../../constants';
import type {
  CustomizationCategory,
  Region,
} from '../../constants/customizationCategories';
import type { AiDisplayImageFragment } from '../../graphql/generated';
import { isGeneratingImage } from '../../helpers';
import type { Segment } from '../../helpers/segmentation';
import type { DesignerProduct } from '../../types';
import { isPaintProduct } from '../../types';

import { instancesToMaterials } from './SegmentationOverlay';
import { useCustomizeReferrerState } from './useCustomizeReferrerState';
import type {
  CustomizationSegmentation,
  Segmentation,
} from './useSegmentation';
import { useSegmentation } from './useSegmentation';

export type CustomizationResult = ReturnType<
  ReturnType<typeof useCustomizeAiImageMutation>[0]
>;

export interface AppliedCustomization {
  product: DesignerProduct;
  region: Region;
  instances?: Segment[];
}

export interface CustomizationHistoryEntry {
  customization: AppliedCustomization | null;
  aiGeneratedImageId: number | null;
  key: number;
  session: number;
}

export interface CustomizationContext {
  baseImage: AiGeneratedImageFragment;
  category: CustomizationCategory | null;
  clearCustomization: () => void;
  customization: AppliedCustomization | null;
  customizeImage: (customization: DesignerProduct, region?: string) => void;
  customizedImage: AiGeneratedImageFragment | null;
  history: {
    activeIndex: number;
    entries: CustomizationHistoryEntry[];
    select: (index: number) => void;
    hasChanges: boolean;
  };
  isCustomizing: boolean;
  onCancel: () => void;
  onApply: () => void;
  segmentation: Segmentation;
  targetImage: AiDisplayImageFragment;
}

const [Provider, useContext] = createContext<CustomizationContext>({
  name: 'CustomizationContext',
});

const CUSTOMIZE_FAILED_TOAST_ID = 'customize-failed-toast';
const CUSTOMIZE_FAILED_TOAST_DURATION = 5000;

const useCustomizationHistory = (baseImage: AiGeneratedImageFragment) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // A history session is used to identify a group of images added within a specific submenu. This enables removing a
  // group of images added within a specific submenu while preserving the rest of the history.
  const [historySession, setHistorySession] = useState(1);
  const [historySessionEntryIndex, setHistorySessionEntryIndex] =
    useState(activeIndex);

  const [history, setHistory] = useState<CustomizationHistoryEntry[]>([
    {
      customization: null,
      aiGeneratedImageId: baseImage.id,
      key: 0,
      session: 0,
    },
  ]);

  // We need a unique identifier for each history entry, and we can't use the
  // `aiGeneratedImageId` because we push to the history before that mutation
  // responds. This is used as the `key` for any UI that displays the history.
  const historyKey = useRef(0);

  const push = useCallback(
    (
      customization: AppliedCustomization,
      aiGeneratedImageId: number | null,
    ) => {
      const newIndex = activeIndex + 1;

      historyKey.current -= 1;

      // Insert new entries to the right of the active index
      setHistory(prevState => [
        ...prevState.slice(0, newIndex),
        {
          customization,
          aiGeneratedImageId,
          key: historyKey.current,
          session: historySession,
        },
        ...prevState.slice(newIndex),
      ]);

      setActiveIndex(newIndex);

      return newIndex;
    },
    [activeIndex, historySession],
  );

  const update = useCallback(
    (
      index: number,
      customization: AppliedCustomization,
      aiGeneratedImageId: number | null,
    ) => {
      setHistory(prevState => [
        ...prevState.slice(0, index),
        { ...prevState[index], customization, aiGeneratedImageId },
        ...prevState.slice(index + 1),
      ]);
    },
    [],
  );

  const removeByIndex = useCallback(
    (index: number) => {
      const updatedHistory = [
        ...history.slice(0, index),
        ...history.slice(index + 1),
      ];

      setHistory(updatedHistory);

      const selectedIndex =
        activeIndex === index && activeIndex > 0
          ? activeIndex - 1
          : activeIndex;

      setActiveIndex(selectedIndex);

      return updatedHistory[selectedIndex];
    },
    [activeIndex, history],
  );

  const selectByIndex = useCallback(
    (index: number) => {
      if (!history[index]) {
        return undefined;
      }

      setActiveIndex(index);

      return history[index];
    },
    [history],
  );

  const removeById = useCallback(
    (id: number) => {
      const index = history.findIndex(item => item.aiGeneratedImageId === id);

      if (index < 0) {
        return history[activeIndex];
      }

      return removeByIndex(index);
    },
    [history, removeByIndex, activeIndex],
  );

  const newSession = useCallback(() => {
    setHistorySession(prevState => prevState + 1);
    setHistorySessionEntryIndex(activeIndex);
  }, [activeIndex]);

  const discardSession = useCallback(() => {
    setActiveIndex(historySessionEntryIndex);
    setHistory(prevState => [
      ...prevState.filter(entry => entry.session !== historySession),
    ]);
    setHistorySession(prevState => prevState + 1);
  }, [historySession, historySessionEntryIndex]);

  return useMemoContextValue({
    activeIndex,
    discardSession,
    entries: history,
    hasChanges: history.length > 1 && activeIndex !== historySessionEntryIndex,
    historySessionEntryIndex,
    newSession,
    push,
    removeById,
    removeByIndex,
    selectByIndex,
    session: historySession,
    update,
    setHistory,
    setActiveIndex,
    setHistorySession,
    setHistorySessionEntryIndex,
  });
};

const useCustomizationErrorToast = () => {
  const t = useHomeownerTranslate();
  const toastBody = t('customize.customizeErrorToastBody');
  const toastButtonText = t('customize.customizeErrorToastButtonText');
  const toast = useToast({
    containerStyle: { width: 'auto' },
  });

  return useCallback(() => {
    toast({
      id: CUSTOMIZE_FAILED_TOAST_ID,
      description: toastBody,
      duration: CUSTOMIZE_FAILED_TOAST_DURATION,
      position: 'top',
      status: 'error',
      props: {
        action: {
          label: toastButtonText,
          callback: () => toast.close(CUSTOMIZE_FAILED_TOAST_ID),
        },
      },
    });
  }, [toastBody, toastButtonText, toast]);
};

export const useCustomization = () => {
  const { segmentation: _, ...customization } = useContext();

  return customization;
};

export const useCustomizationSegmentation = (): CustomizationSegmentation => {
  const { handleFunctionsRef: _, ...segmentation } = useContext().segmentation;

  return segmentation;
};

export const useCustomizationSegmentHandleFunctionsRef = () =>
  useContext().segmentation.handleFunctionsRef;

export interface CustomizationProviderProps
  extends PropsWithChildren<{}>,
    Pick<CustomizationContext, 'targetImage' | 'category'> {
  segmentationData: AiGeneratedImageSegmentationQuery | undefined;
}

export const CustomizationProvider = ({
  category,
  targetImage,
  segmentationData,
  ...props
}: CustomizationProviderProps) => {
  const baseImage = targetImage.aiGeneratedImage;
  const [customization, setCustomization] =
    useState<CustomizationContext['customization']>(null);

  const segmentation = useSegmentation({
    query: segmentationData,
    region: category?.region,
  });

  const customizationHistory = useCustomizationHistory(baseImage);
  const toastCustomizationError = useCustomizationErrorToast();

  const [customizeAiImage, { loading: isMutationLoading }] =
    useCustomizeAiImageMutation();

  const currentHistoryItem =
    customizationHistory.entries.length > 0
      ? customizationHistory.entries[customizationHistory.activeIndex]
      : undefined;

  const { data: customizedImageData, loading: isQueryLoading } =
    useAiGeneratedImageQuery({
      variables: {
        id: String(currentHistoryItem?.aiGeneratedImageId ?? baseImage.id),
      },
      skip: currentHistoryItem?.aiGeneratedImageId === null,
    });

  // On generation failure, remove the failure from history
  useEffect(
    () => {
      if (
        customizedImageData?.aiGeneratedImage?.status ===
          GeneratedImageStatus.FAILED ||
        customizedImageData?.aiGeneratedImage?.clientStatus ===
          GeneratedImageStatus.TIMED_OUT
      ) {
        toastCustomizationError();
        const selected = customizationHistory.removeById(
          customizedImageData.aiGeneratedImage.id,
        );
        setCustomization(selected.customization);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      customizedImageData?.aiGeneratedImage?.id,
      customizedImageData?.aiGeneratedImage?.status,
      customizedImageData?.aiGeneratedImage?.clientStatus,
      customizationHistory,
      // TODO: Figure out why this toast is not stable and is causing a re-trigger of this effect
      // toastCustomizationError,
    ],
  );

  const customizedImage =
    currentHistoryItem?.customization && customizedImageData?.aiGeneratedImage
      ? customizedImageData?.aiGeneratedImage
      : null;

  const clearCustomization = useCallback(() => {
    setCustomization(null);
  }, []);

  useEffect(() => {
    if (segmentation.selection.length > 0) setCustomization(null);
  }, [segmentation.selection]);

  const isCustomizing =
    isMutationLoading ||
    isQueryLoading ||
    isGeneratingImage(customizedImage) ||
    // We briefly insert empty history entries as placeholders while
    // the `customizeAiImage` mutation is in flight, so this prevents
    // this flag from briefly flipping to false during that mutation.
    (customizedImage === null && customizationHistory.activeIndex > 0);

  const selectHistoryEntry = useCallback(
    (index: number) => {
      let adjustedIndex = index;

      if (isCustomizing) {
        // Since we are re-indexing the history stack by removing an entry,
        // we need to adjust the selected index
        if (index > customizationHistory.activeIndex) {
          adjustedIndex -= 1;
        }
        // If a customization is currently in flight and the user selects a
        // different history entry, remove the in flight customization
        customizationHistory.removeByIndex(customizationHistory.activeIndex);
      }

      const selected = customizationHistory.selectByIndex(adjustedIndex);

      if (selected) {
        setCustomization(selected.customization);

        if (selected.customization?.instances) {
          const { instances } = selected.customization;

          segmentation.setSelection(
            segmentation.handlePlacementMode === 'material'
              ? instancesToMaterials(instances)
              : instances.map(({ id }) => id),
          );
        }
      }
    },
    [customizationHistory, isCustomizing, segmentation],
  );

  const history = useMemoContextValue({
    activeIndex: customizationHistory.activeIndex,
    entries: customizationHistory.entries,
    hasChanges: customizationHistory.hasChanges,
    select: selectHistoryEntry,
  });

  const customizeImage = useCallback(
    async (product: DesignerProduct) => {
      const { product: currentProduct } = customization ?? {};
      const region = category?.region ?? 'walls';

      const { selection, selectionMode, showSelectionRequired } = segmentation;

      if (selectionMode === 'instance' && selection.length === 0) {
        showSelectionRequired();
        return;
      }

      // Do not apply customization if the customization is
      // the same as the currently generating customization
      if (
        product.$type === currentProduct?.$type &&
        product.id === currentProduct?.id &&
        isCustomizing
      ) {
        return;
      }

      const instances = segmentation.selectedSegments ?? undefined;
      const applied = { product, region, instances };

      setCustomization(applied);

      let historyIndex = customizationHistory.activeIndex;

      if (!isCustomizing) {
        historyIndex = customizationHistory.push(applied, null);
      } else {
        // If there is already an in flight customization, update that
        // history entry rather than creating a new history entry
        customizationHistory.update(historyIndex, applied, null);
      }

      const commonVariables = {
        appId: AI_GENERATION_APP_ID,
        region,
        instances: instances?.map(({ id }) => id) ?? undefined,
        aiGeneratedImageId:
          currentHistoryItem?.aiGeneratedImageId ?? baseImage.id,
      } satisfies CustomizeAiImageMutationVariables;

      const productVariables =
        'productCatalogVariationUuid' in product
          ? ({
              aiPromptName: isPaintProduct(product)
                ? undefined
                : product.aiPromptName,
              productCatalogVariationUuid: product.productCatalogVariationUuid,
              paintHexColor: isPaintProduct(product) ? product.hex : undefined,
              materialHexColor: !isPaintProduct(product)
                ? product.hex
                : undefined,
            } satisfies Partial<CustomizeAiImageMutationVariables>)
          : ({
              paintColorId: isPaintProduct(product) ? product.id : undefined,
              materialId: !isPaintProduct(product) ? product.id : undefined,
            } satisfies Partial<CustomizeAiImageMutationVariables>);

      const result = await customizeAiImage({
        variables: {
          ...commonVariables,
          ...productVariables,
        },
      });

      if (result.errors && result.errors.length > 0) {
        toastCustomizationError();
        const selected = customizationHistory.removeByIndex(historyIndex);
        setCustomization(selected.customization);
      }

      if (product && result.data?.customizeAiImage) {
        customizationHistory.update(
          historyIndex,
          applied,
          result.data.customizeAiImage.id,
        );
      }
    },
    [
      baseImage.id,
      category?.region,
      currentHistoryItem?.aiGeneratedImageId,
      customization,
      customizationHistory,
      customizeAiImage,
      isCustomizing,
      segmentation,
      toastCustomizationError,
    ],
  );

  const routerHistory = useHistory();
  const state = useCustomizeReferrerState();

  const close = useCallback(() => {
    routerHistory.push(
      generatePath(Route.HOMEOWNER_APP_CUSTOMIZE.path, {
        aiDisplayImageId: targetImage.id,
      }),
      state,
    );
  }, [routerHistory, state, targetImage.id]);

  const onApply = useCallback(() => {
    customizationHistory.newSession();
    close();
  }, [close, customizationHistory]);

  const onCancel = useCallback(() => {
    const restoredCustomization =
      customizationHistory.entries[
        customizationHistory.historySessionEntryIndex
      ].customization;
    setCustomization(restoredCustomization);
    customizationHistory.discardSession();
    close();
  }, [close, customizationHistory]);

  const overrideMaxPollingTime = 45000; // 45 seconds

  useEffect(() => {
    setCustomization(null);

    customizationHistory.setHistory([
      {
        customization: null,
        aiGeneratedImageId: baseImage.id,
        key: 0,
        session: 0,
      },
    ]);
    customizationHistory.setActiveIndex(0);
    customizationHistory.setHistorySession(1);
    customizationHistory.setHistorySessionEntryIndex(0);

    segmentation.resetSelection();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseImage.id]); // we only want this to run if baseImage.id changes

  return (
    <>
      <PollGeneratedImage
        consumerId="customize"
        image={customizedImage}
        overrideMaxPollingTime={overrideMaxPollingTime}
      />
      <Provider
        value={useMemoContextValue({
          baseImage,
          category,
          clearCustomization,
          customization,
          customizeImage,
          customizedImage,
          history,
          isCustomizing,
          onApply,
          onCancel,
          segmentation,
          targetImage,
        })}
        {...props}
      />
    </>
  );
};
